import { getSdk } from "./sdkService";
import { config } from "../config/config";
import { checkEpisodeData, getSeasonEpisodeFromMediaGuid } from "./titleDataService";
import { getProgressData } from "./mediaListService";
import { getEpisodeLabel } from "./copyService";
import padStart from "../utils/padStart";

const HAS_4K = "4kProgramId";
const STREAMING_URL = "streamingUrl";
const RELEASE_PID = "releasePid";
const RELEASE_URL = "releaseUrl";
const DURATION = "duration";
const TITLE = "title";
const SEASON_NUMBER = "tvSeasonNumber";
const EPISODE_NUMBER = "tvSeasonEpisodeNumber";
const SHORT_DESCRIPTION = "shortDescription";
const ID = "id";

/**
 * Gets episode media content either from last watched episode (in case there is one) or
 * the first of the episodes list. Needs a React component class context since
 * this method is making use of inner context properties.
 * @returns {Object} - Episode media content.
 * @public
 */
async function getEpisodeMediaContent(is4K) {
  const lastSeenEpisode = is4K ? await getEpisode4KData.call(this) : this.lastSeenEpisode;
  const episode = lastSeenEpisode || this.episodesList[0];
  return episode.mediaContent[0].content;
}

/**
 * Gets episode video data properties from last seen episode in case it exists (from watchlist) or
 * from the first episode available in the episodes list. Needs a React component class context since
 * this method is making use of inner context properties.
 * @param {String} property - Property to be found within title video content object.
 * @param {Boolean} is4K - Condition that determines if content is suitable for 4K playback.
 * @returns {String|Array} - Value of the received property to be found within video content object.
 * @public
 */
async function getEpisodeVideoData(property, is4K) {
  const episodeMedia = await getEpisodeMediaContent.call(this, is4K);
  return episodeMedia.video[property];
}

/**
 * Returns external subtitles from last seen episode in case it exists (from watchlist) or
 * from the first episode available in the episodes list. Needs a React component class context since
 * this method is making use of inner context properties.
 * @param {Boolean} is4K - Condition that determines if content is suitable for 4K playback.
 * @returns {Object} - Object containing the languages available subtitles with their URLs to make
 * the request to retrieve them.
 * @public
 */
async function getEpisodeExternalSubtitles(is4K) {
  const episodeMedia = await getEpisodeMediaContent.call(this, is4K);
  return episodeMedia.subtitles;
}

/**
 * Gets episode filmstrip data from last seen episode in case it exists (from watchlist) or
 * from the first episode available in the episodes list. Needs a React component class context since
 * this method is making use of inner context variables.
 * @param {Boolean} is4K - Condition that determines if content is suitable for 4K playback.
 * @returns {String} - String containing the URL to make the request to retrieve filmstrips.
 * @private
 */
async function getEpisodeFilmstripData(is4K) {
  const episode = is4K
    ? await getEpisode4KData.call(this)
    : this.lastSeenEpisode
    ? this.lastSeenEpisode
    : this.episodesList[0];
  return episode.mediaContent[0].content.filmstrip;
}

/**
 * Gets episode title 4K related content either from last watched episode (in case there is one) or
 * the first of the episodes list that contain it. Needs a React component class context since
 * this method is making use of inner context properties.
 * @returns {Object} - Episode title 4K related content.
 * @public
 */
async function getEpisode4KData() {
  const sdk = await getSdk();
  return this.lastSeenEpisode && this.lastSeenEpisode.title4K
    ? this.lastSeenEpisode.title4K
    : await sdk.mediaManager.getTitle(this.episodesList[0][HAS_4K], { is4K: true });
}

/**
 * Gets trailer media related data to be displayed on player. Needs a React component class context since
 * this method is making use of this.state.
 * @returns {Object} - Trailer data object to be displayed on the player.
 * @public
 */
export function getTrailerVideoData() {
  const playbackAnalytics = config.get().featureToggles.playbackAnalytics;
  return {
    videoType: "trailer",
    manifest: this.state.trailer,
    title: this.state.title,
    id: this.state.id,
    duration: playbackAnalytics && playbackAnalytics.trailerFixedDuration // This is due to a limitation in Youbora. In order to avoid an extra call to endpoint "init" from youbora we have to send duration
  };
}

/**
 * Gets the video content related data to pass it to the player. Needs a React
 * component class context since this method is making use of inner context properties.
 * @param {Boolean} is4K - Condition that determines if content is suitable for 4K playback.
 * @returns {Object} Object to be passed to player containing all the necessary data to be displayed or
 * to reproduce the content.
 * @public
 */
export async function getVideoDataForPlayer(is4K) {
  const currentHas4K =
    is4K && // Requested 4K play AND
    ((!this.isSeries && this.mediaData.media4K) || // Is Movie and has media4K OR
      !!((this.lastSeenEpisode && this.lastSeenEpisode[HAS_4K]) || this.episodesList[0][HAS_4K])); //// Is Series AND (Is resumed OR first episode has 4k)
  const movieVideo = currentHas4K ? this.mediaData.video4K : this.mediaData.videoHD;
  const episode = this.isSeries && (this.lastSeenEpisode || this.episodesList[0]);
  return !this.isSeries
    ? {
        videoType: "movie",
        manifest: movieVideo.streamingUrl,
        releasePid: movieVideo.releasePid,
        releaseUrl: movieVideo.releaseUrl,
        duration: movieVideo.duration,
        filmstrip: currentHas4K ? undefined : this.mediaData.mediaHD.content.filmstrip,
        title: this.state.title,
        description: this.state.description,
        playbackTime: this.isRestarting ? null : getProgressData.call(this),
        id: this.state.id,
        has4K: this.state.has4K,
        externalSubtitles: currentHas4K
          ? this.mediaData.media4K.content.subtitles
          : this.mediaData.mediaHD.content.subtitles,
        mediaGuid: currentHas4K ? this.mediaData.media4K.mediaGuid : this.mediaData.mediaHD.mediaGuid,
        genre: this.state.titleData.genres[0],
        addonContent: this.state.addonContent,
        isLive: this.state.isLive
      }
    : {
        videoType: "series",
        manifest: await getEpisodeVideoData.call(this, STREAMING_URL, currentHas4K),
        releasePid: await getEpisodeVideoData.call(this, RELEASE_PID, currentHas4K),
        releaseUrl: await getEpisodeVideoData.call(this, RELEASE_URL, currentHas4K),
        duration: await getEpisodeVideoData.call(this, DURATION, currentHas4K),
        filmstrip: await getEpisodeFilmstripData.call(this, currentHas4K),
        description: checkEpisodeData.call(this, SHORT_DESCRIPTION),
        title: checkEpisodeData.call(this, TITLE),
        tvSeasonNumber: checkEpisodeData.call(this, SEASON_NUMBER),
        tvSeasonEpisodeNumber: checkEpisodeData.call(this, EPISODE_NUMBER),
        playbackTime: this.isRestarting ? null : getProgressData.call(this),
        seriesTitle: this.state.title,
        episodeLabel: getEpisodeLabel(episode.tvSeasonNumber, episode.tvSeasonEpisodeNumber),
        seasonsList: this.seasonsList,
        episodesList: this.episodesList,
        id: checkEpisodeData.call(this, ID),
        seriesId: this.state.id,
        has4K: this.state.has4K,
        //externalSubtitles: currentHas4K && this.state.externalSubtitles
        externalSubtitles: await getEpisodeExternalSubtitles.call(this, currentHas4K),
        mediaGuid: currentHas4K ? this.mediaData.media4K.mediaGuid : this.mediaData.mediaHD.mediaGuid,
        credits: currentHas4K ? this.mediaData.media4K.credits : this.mediaData.mediaHD.credits,
        genre: this.state.titleData.genres[0],
        addonContent: this.state.addonContent,
        isLive: this.state.isLive
      };
}

/**
 * Gets media data object depending on whether it is a series and it has been started watching (in this case
 * it will check last seen episode data) or a movie/not started series. Needs a React
 * component class context since this method is making use of inner context properties.
 * @param {Object} title - Title data object
 * @param {Boolean} is4K - The title should be 4k
 * @returns {Object} Title media content object.
 * @public
 */
export function getMediaContentData(title, is4K) {
  if (this.lastSeenEpisode) {
    // Is resumed playback if series
    const lastSeenEpisode = !is4K ? this.lastSeenEpisode : this.lastSeenEpisode.title4K;
    return lastSeenEpisode && lastSeenEpisode.mediaContent[0];
  }
  // Is fresh start playback or movie
  // If 4k series check whether the first available episode has 4k media.
  const firstEpisode = this.isSeries && `S${padStart(this.seasonsListRaw[0].tvSeasonNumber, 2)}E001`;
  const firstEpisodeFromMediaGuid =
    this.isSeries &&
    title.mediaContent.find(content => getSeasonEpisodeFromMediaGuid(content.mediaGuid) === firstEpisode);
  return !is4K || !this.isSeries
    ? title.mediaContent[0]
    : firstEpisodeFromMediaGuid
    ? firstEpisodeFromMediaGuid
    : undefined;
}
